import React from 'react';
import { format } from 'date-fns';
import { Typography, Select } from '@deeptrue-frontend/ui';
import styled from 'styled-components/macro';
import { useFormatMessage } from 'components/IntlProvider';

const SatisfactionNote = styled(Typography)`
  font-size: 0.875rem !important;
  color: #42526e;
  margin-bottom: 1.5rem !important;
  span {
    display: block;
  }
`;

const SummaryReportTitle = styled(Typography)`
  font-weight: 600;
  line-height: 1.25rem;
`;

const Ol = styled.ol`
  font-family: Inter, Helvetica;
`;

const Li = styled.li`
  font-family: Inter, Helvetica;
`;

const SummaryReport = styled.div`
  background-color: #fff;
  border: 1px solid #e3e5e8;
  padding: 1rem;
  border-radius: 4px;
  margin-top: 0.5rem;
`;

const SatisfactionNotes = styled.div`
  h3 {
    white-space: nowrap;
    margin-bottom: 2rem;
  }

  & > ol {
    margin-top: 2rem;
    & > li {
      margin-bottom: 1.5rem;
      position: relative;

      &:before {
        content: '';
        position: absolute;
        background-color: ${({ theme }) => theme.colors.royalBlue};
        height: 1.5rem;
        top: 0;
        left: -2rem;
        width: 2px;
      }

      & > p {
        font-weight: 500;
      }

      & > ol {
        padding-left: 1rem;
        margin-top: 1.5rem;

        & > li {
          margin-bottom: 0.5rem;
        }
      }
    }
  }
`;

interface Props {
  totalCompleted: number | null;
  dataToSet: any[];
  setReportType: (type: '1' | '2') => void;
  reportType: '1' | '2';
}

const Legend = ({
  totalCompleted,
  dataToSet,
  reportType,
  setReportType,
}: Props) => {
  const t = useFormatMessage();
  return (
    <SatisfactionNotes>
      <Typography mt="2rem" mb="0.5rem" variant="copy2" weight="bold">
        {t('customerSatisfaction.reportType.title', 'Type of Report')}
      </Typography>
      <Select
        name="report_type"
        onChange={(_name: string, value: any) => {
          setReportType(value.toString());
        }}
        value={reportType}
        options={[
          {
            label: t('customerSatisfaction.title', 'Summarized Report'),
            value: '1',
          },
          {
            label: t(
              'customerSatisfaction.reportType.individual',
              'Individual Report'
            ),
            value: '2',
          },
        ]}
      />
      <SummaryReport>
        <SatisfactionNote>
          <SummaryReportTitle>
            {t(
              'customerSatisfaction.note.respondants',
              'Number of respondants'
            )}
          </SummaryReportTitle>

          <span>{totalCompleted || 0}</span>
        </SatisfactionNote>
        <SatisfactionNote>
          <SummaryReportTitle>
            {t('customerSatisfaction.note.time', 'Time period')}
          </SummaryReportTitle>

          <span>
            {t('customerSatisfaction.note.fromTo', '{ from } to { to }', {
              from: format(new Date(), 'dd-MM-yyyy'),
              to: format(new Date(), 'dd-MM-yyyy'),
            })}
          </span>
        </SatisfactionNote>
      </SummaryReport>
      <Ol>
        {dataToSet.map((set, key) => (
          <Li key={key}>
            <Typography>{set.name}</Typography>
            {set.groups && (
              <Ol type="a">
                {set.groups.map((group: any, key: any) => (
                  <Li key={key}>
                    <Typography>{group.name}</Typography>
                  </Li>
                ))}
              </Ol>
            )}
            {set.values && (
              <Ol type="a">
                {set.values.map((value: any, key: any) => (
                  <Li key={key}>
                    <Typography>{value.name}</Typography>
                  </Li>
                ))}
              </Ol>
            )}
          </Li>
        ))}
      </Ol>
    </SatisfactionNotes>
  );
};

export default Legend;
